import React from 'react';
import Button from '@material-ui/core/Button';
import ReviewTypeQuestions from './ReviewTypeQuestions';
import InProgressReviewActions from 'actions/coaching/InProgressReviewActions';
import InProgressReview from 'records/InProgressReview';
import {List, Map, Set} from 'immutable';
import CoachingReviewActions from 'actions/coaching/CoachingReviewActions';
import confirm from 'utils/confirm';
import noop from 'lodash/noop';
import {browserHistory} from 'react-router';
import './SubmitReviewFeedback.styl';
import ReviewFileUploads from 'components/coaching/ReviewFileUploads';
import CoachingPlaylistActions from 'actions/coaching/CoachingPlaylistActions';
import CoachingReviewStatusRibbon from 'components/coaching/widgets/CoachingReviewStatusRibbon';
import connectToStores from 'alt-utils/lib/connectToStores';
import InProgressReviewStore from 'stores/coaching/InProgressReviewStore';
import {associateBy} from 'utils/associateBy';
import CoachingReviewInfoBar from 'components/coaching/CoachingReviewInfoBar';
import RichTextEditor from 'components/pure/utils/form/user-taggable-input/RichTextEditor';
import ReviewTypeSelect from 'components/coaching/ReviewTypeSelect';
import UpcomingReviewsActions from 'actions/coaching/UpcomingReviewsActions';
import CoachingPlanActions from 'actions/coaching/CoachingPlanActions';
import {RESPONDENT_TYPE} from 'records/CoachingReviewTypeQuestion';
import anyQuestionHasAnAnswer from 'components/coaching/utils/anyQuestionHasAnAnswer';
import GenericTooltip from 'components/pure/tooltips/GenericTooltip';
import * as queryParams from 'utils/queryParams';
import RepProfilePageActions from 'actions/RepProfilePageActions';
import CoachingReviewStore from 'stores/coaching/CoachingReviewStore';

import GoalsKpiEditableList from './goals-kpi-editable-list';
import hasFlag from 'utils/hasFlag';
import {featureFlags} from 'utils/const';
import {getDashboardPath, TOOLTIP_DELAY} from 'components/StatsPage2.utils';
import UPCOMING_REVIEW_ROLES from 'actions/coaching/UPCOMING_REVIEW_ROLES';
import {scrollToTop} from 'utils/scrollToTop';

export const START_LABEL_COACH = 'Start Meeting';

const IN_PROGRESS_TIMER_IN_MS = 1000;

@connectToStores
export default class SubmitReviewFeedback extends React.PureComponent {
  static getStores() {
    return [InProgressReviewStore];
  }

  static getPropsFromStores({review}) {
    const inProgressReview = InProgressReviewStore.getState().inProgressReviews.get(review.id + '');
    const reviewTypeAnswers = CoachingReviewStore.getState().coachingReviewAnswers.get(
      review && review?.id,
      List()
    );

    return {
      inProgressReview,
      reviewTypeAnswers,
    };
  }

  constructor(props) {
    super(props);
    this.saveInProgressReview = this.saveInProgressReview.bind(this);
    this.clearInProgressReview = this.clearInProgressReview.bind(this);
    this.onStartReview = this.onStartReview.bind(this);
    this.startReview = this.startReview.bind(this);
    this.onCancel = this.onCancel.bind(this);
    this.onChangeReviewType = this.onChangeReviewType.bind(this);
    this.onPrepReview = this.onPrepReview.bind(this);
    this.onCoachOverride = this.onCoachOverride.bind(this);

    const answers =
      props.review.reviewTypeAnswers &&
      associateBy(props.review.reviewTypeAnswers, a => a.reviewQuestionId);

    this.state = {
      inProgress: false,
      disabled: true,
      submitting: false,
      isPrepping: false,
      // this allows the coach to un-disable and fill out the rep's questions
      coachOverrideRepQuestions: false,
      reviewFileUploads: props.reviewFileUploads || List(),
      reviewTypeAnswers: answers || Map(),
      generalComments: props.review.comment || '',
      // user specified review type. provide reasonable defaults upon page load
      // see mount/componentWillReceiveProps, getPreselectedReviewTypeId
      coachingReviewTypeId: undefined,
      errors: Map(), // review question id to error message.
      initializedReviewTypeAnswersState: false,
    };
  }

  componentDidMount() {
    const {autoStartReview, inProgressReview, review, plan} = this.props;

    if (autoStartReview) {
      this.setState({disabled: false});

      if (plan.adHoc) {
        this.onCoachOverride();
        scrollToTop();
      }
    }

    // if we have an in-progress review, set currently filling out review
    if (inProgressReview) {
      const isPrepping = inProgressReview.isPrepping;
      this.setState({disabled: false});
      CoachingReviewActions.setCurrentlyFillingOutReview.defer(inProgressReview.id);

      this.startAutoSave({isPrepping});
    } else if (autoStartReview) {
      this.startAutoSave({isPrepping: false});
    }

    this.setState({
      coachingReviewTypeId: review.reviewTypeId,
    });
  }

  /**
   * If we're in "form disabled" state (viewing the review), listen to the global
   * stores in order to receive adds/deletes to review <> call relationships.
   *
   * Once we start filling out the review - we're locked in to those relationships.
   * this.state tracks review score & comments.
   */
  UNSAFE_componentWillReceiveProps({reviewFileUploads, reviewTypeAnswers}) {
    const {inProgressReview} = this.props;
    const {reviewFileUploads: stateFileUploads, disabled} = this.state;
    if (inProgressReview) {
      // Somebody's already filling it out, prefer their fileuploads.
      // However, if there are new ones coming in, throw them into the mix
      // (a new livecoaching call, most likely)
      const newFileuploads = reviewFileUploads
        ? reviewFileUploads.filter(rfu => !stateFileUploads?.find(sfu => sfu.id === rfu.id))
        : List();
      if (newFileuploads.size > 0) {
        this.setState({
          reviewFileUploads: List([...stateFileUploads, ...newFileuploads]),
        });
      }

      //
      // If we have reviewTypeAnswers, assume answers from
      // CoachingReviewStore since the meeting must have start/prep
      // from the listening assistant drawer with previously supplied
      // answers. So get them into state. Only once ! ! !
      //
      if (!this.state.initializedReviewTypeAnswersState && reviewTypeAnswers?.size > 0) {
        const answers =
          reviewTypeAnswers && associateBy(reviewTypeAnswers, a => a.reviewQuestionId);

        this.setState({
          reviewTypeAnswers: answers,
          initializedReviewTypeAnswersState: true,
        });
      }
    } else {
      if (disabled) {
        this.setState({
          reviewFileUploads: reviewFileUploads || List(),
        });
      }
    }
  }

  componentWillUnmount() {
    // clear timer no matter what.
    clearInterval(this.saveInProgressTimer);
    if (!this.state.disabled && this.state.inProgress) {
      // if we were in the process of filling out, save one last time
      this.saveInProgressReview();
    }

    CoachingReviewActions.clearCurrentlyFillingOutReview.defer();
  }

  endPlan(plan) {
    const {review, sessionUser} = this.props;

    confirm('Are you sure you want to cancel this review?').then(async () => {
      await CoachingPlanActions.endPlan(plan.id);

      this.clearInProgressReview(review, sessionUser, browserHistory.goBack);
    }, noop);
  }

  saveInProgressReview() {
    const {review, plan, sessionUser} = this.props;
    const {
      isPrepping,
      reviewFileUploads,
      generalComments,
      reviewTypeAnswers,
      coachingReviewTypeId,
    } = this.state;

    const inProgressReview = new InProgressReview({
      id: review.id,
      isPrepping,
      scheduleAt: review.scheduleAt,
      planId: review.planId,
      recipientId: plan.recipient.id,
      reviewFileUploads: reviewFileUploads,
      comment: generalComments,
      reviewTypeAnswers: reviewTypeAnswers,
      coachingReviewTypeId,
      adHoc: plan.adHoc,
    });

    InProgressReviewActions.setInProgressReview.defer(inProgressReview, sessionUser);
  }

  clearInProgressReview(review, sessionUser, callback) {
    clearInterval(this.saveInProgressTimer);
    InProgressReviewActions.deleteInProgressReview(review.id, sessionUser);

    this.setState({inProgress: false}, callback);
  }

  startReview() {
    const {isCoach} = this.props;

    CoachingReviewActions.startReview(this.props.review.id, isCoach);
    this.startAutoSave({isPrepping: false});
  }

  onPrepReview() {
    this.setState({disabled: false});
    CoachingReviewActions.prepReview(this.props.review.id);
    this.startAutoSave({isPrepping: true});
    CoachingPlaylistActions.hide();
  }

  /**
   * We autosave into localstorage on a timer.
   * Make sure to save on the leading edge as well - so we set up the fact it is in progress
   * in case the user navs away immediately
   *
   * @param isPrepping
   */
  startAutoSave({isPrepping}) {
    const {reviewFileUploads, inProgressReview} = this.props;

    if (inProgressReview) {
      this.setState(
        {
          isPrepping: isPrepping,
          reviewFileUploads: inProgressReview.reviewFileUploads,
          generalComments: inProgressReview.comment,
          reviewTypeAnswers: inProgressReview.reviewTypeAnswers,
          coachingReviewTypeId: inProgressReview.coachingReviewTypeId,
        },
        () => {
          this.saveInProgressReview();
          this.saveInProgressTimer = setInterval(
            this.saveInProgressReview,
            IN_PROGRESS_TIMER_IN_MS
          );
        }
      );
    } else {
      this.setState({reviewFileUploads: reviewFileUploads ?? List(), isPrepping}, () => {
        this.saveInProgressReview();
        this.saveInProgressTimer = setInterval(this.saveInProgressReview, IN_PROGRESS_TIMER_IN_MS);
      });
    }
  }

  onCoachOverride() {
    this.setState({coachOverrideRepQuestions: true});
  }

  onSubmit(review, reviewFileUploads, sessionUser, plan, reviewType) {
    const {isCoach, rectifyPlaylist} = this.props;
    const {isPrepping} = this.state;
    const validatedAnswers = this.validate(reviewType, true);

    if (!validatedAnswers) {
      return;
    }

    this.setState({submitting: true}, () => {
      this.clearInProgressReview(review, sessionUser, async () => {
        if (!reviewType.includesCalls) {
          await Promise.all(
            reviewFileUploads.map(r => {
              return CoachingReviewActions.deleteReviewFileUpload(review.id, r.fileuploadId);
            })
          );
        }

        // Only the coach can add general comments and complete the review.
        if (isCoach) {
          await CoachingReviewActions.updateReview(
            review
              .set('reviewTypeId', reviewType.id)
              .set('status', isPrepping ? review.status : 'COMPLETED')
              .set('comment', this.state.generalComments)
          );
        }
        // include reviewFileuploadId in reviewFeedbackList
        const reviewFiles = reviewType.includesCalls
          ? reviewFileUploads
              // remove livecoaching when saving/updating
              .filter(r => !r.livecoaching)
              .map(r =>
                r.set(
                  'reviewFeedbackList',
                  r.reviewFeedbackList.map(f => f.set('reviewFileuploadId', r.id))
                )
              )
          : List();
        await CoachingReviewActions.submitFeedback(review, reviewFiles, validatedAnswers);
        // Disable to get start button working again so that recipients (who can't complete the
        // review and thus never see the summary with the edit button) can edit.
        this.setState({disabled: true});

        if (!isCoach) {
          RepProfilePageActions.selectTabIndex(0);
          browserHistory.push(getDashboardPath(sessionUser));
        } else {
          this.props.onSubmit(review);
        }
        rectifyPlaylist();
      });
    });
  }

  /**
   * Scrub the answers in state, returning only those that were answered by the current user.
   * This is necessary to avoid validation errors on the server side (the server will not accept
   * answers to questions another participant must answer from this participant).
   *
   * They must be in state so that we can display them.
   *
   * @param {CoachingReviewType} reviewType the type of this review.
   * @param {boolean} override - coaches can override rep answers - if so, perform no scrubbing
   * @return the answers provided by the current user.
   */
  scrubAnswersForSubmission(reviewType, override) {
    const {isCoach} = this.props;
    const {reviewTypeAnswers} = this.state;
    const respondentByQuestionId = Map(reviewType.questions.map(q => [q.id, q.respondent]));
    return reviewTypeAnswers.filter((answer, questionId) => {
      if (override) {
        return true;
      }

      const respondent = respondentByQuestionId.get(questionId);
      return respondent === (isCoach ? RESPONDENT_TYPE.COACH : RESPONDENT_TYPE.REP);
    });
  }

  /**
   * Get the list of answers if validation succeeds. Returns null and sets error state if any
   * answers are invalid.
   *
   * @param {CoachingReviewType} reviewType the type of this review.
   * @param {Boolean} storeErrors if true, store errors in state. Otherwise, just perform validation checking.
   * @returns {null|Immutable.Seq.Indexed<any>} the answers provided by the current user, or null
   * if any are invalid.
   */
  validate(reviewType, storeErrors) {
    const {isCoach} = this.props;
    const {coachOverrideRepQuestions} = this.state;
    const requiredQuestionsByQuestionId = Map(
      reviewType.questions
        .filter(
          question =>
            question.respondent === (isCoach ? RESPONDENT_TYPE.COACH : RESPONDENT_TYPE.REP) &&
            !question.optional
        )
        .map(question => [question.id, question])
    );
    const respondentAnswers = this.scrubAnswersForSubmission(
      reviewType,
      isCoach && coachOverrideRepQuestions
    );

    // Build a list of questions that are required and have answers.
    const idsOfQuestionsWithGoodAnswers = respondentAnswers
      .filter((answer, questionId) => {
        const question = requiredQuestionsByQuestionId.get(questionId);
        if (!question) {
          return true; // not required.
        }
        if (question.isText()) {
          return answer.comment;
        }
        return true; // They must have selected _something_, even N/A. That's fine.
      })
      .keySeq()
      .toSet();

    // Get required questions missing answers, map id to error message.
    const errors = requiredQuestionsByQuestionId
      .filter((_, questionId) => !idsOfQuestionsWithGoodAnswers.has(questionId))
      .map(_ => 'A response is required');

    if (errors.isEmpty()) {
      return respondentAnswers.valueSeq();
    } else {
      if (storeErrors) {
        this.setState({errors});
      }
      return null;
    }
  }

  onCancel() {
    const {disabled, isPrepping} = this.state;

    const {
      review,
      sessionUser,
      reviewFileUploads: reviewFileUploadsFromProps,
      editFeedback,
      rectifyPlaylist,
      isCoach,
    } = this.props;

    // if we're viewing, this button takes us back to the manager's dashboard.
    // however, if we're filling out, take us back to viewing instead
    // and clear contents of the form.
    disabled
      ? this.clearInProgressReview(review, sessionUser, () => browserHistory.goBack())
      : confirm(
          editFeedback || !isCoach
            ? 'Are you sure you want to discard edits?'
            : isPrepping
            ? 'You will lose all of your responses. Are you sure you want to cancel?'
            : 'You will lose all of your responses, and reset the coaching time recorded for this meeting. Are you sure you want to cancel?',
          editFeedback || !isCoach
            ? 'Confirm Cancel Editing'
            : isPrepping
            ? 'Confirm Cancel Prep'
            : 'Confirm Cancel Meeting'
        ).then(() => {
          this.clearInProgressReview(review, sessionUser, () => {
            this.setState(
              {
                disabled: true,
                isPrepping: false,
                coachOverrideRepQuestions: false,
                reviewTypeAnswers: review.reviewTypeAnswers
                  ? associateBy(review.reviewTypeAnswers, a => a.reviewQuestionId)
                  : Map(),
                generalComments: review.comment,
                reviewFileUploads: reviewFileUploadsFromProps ?? List(),
              },
              () => rectifyPlaylist()
            );

            // Set started_at in DB to null if coach and not prepping or editing
            if (isCoach && !isPrepping && !editFeedback) {
              CoachingReviewActions.cancelReview.defer(review.id);
            }

            CoachingReviewActions.clearCurrentlyFillingOutReview.defer();

            scrollToTop();
          });
        });

    queryParams.set(
      {
        autoStartReview: null,
      },
      true
    );
  }

  /**
   * Determines when to disable the submit button.
   * Currently flaky but can't reproduce the issues. Once found, let's turn off the console.warn's
   *
   * @param isCoach
   * @param reviewType
   * @param reviewTypeAnswers
   * @param reviewFileUploads
   * @return {boolean}
   */
  blockSubmit(isCoach, reviewType, reviewTypeAnswers, reviewFileUploads) {
    if (!reviewType) {
      console.warn('blocking submit due to lack of review type');
      return true; // can't submit w/o review type.
    }
    const questionAnswersAreNotValid = !this.validate(reviewType, false);

    if (!isCoach) {
      console.warn(`!isCoach, checking questionAnswersAreNotValid: ${questionAnswersAreNotValid}`);
      // Just ensure question answers are valid for reps.
      return questionAnswersAreNotValid;
    }
    const hasNoReviewFileUploads = !reviewFileUploads || reviewFileUploads.size === 0;
    const hasNoGeneralComments = !this.state.generalComments;
    const hasNoReviewAnswers = !reviewTypeAnswers || reviewTypeAnswers.size <= 0;

    const block =
      questionAnswersAreNotValid ||
      (hasNoReviewFileUploads && hasNoGeneralComments && hasNoReviewAnswers);

    if (block) {
      if (hasNoReviewFileUploads) {
        console.warn('blocking submit due to no review fileuploads');
      }

      if (hasNoGeneralComments) {
        console.warn('blocking submit due to no general comments');
      }

      if (hasNoReviewAnswers) {
        console.warn('blocking submit due to no review answers');
      }
    }

    return block;
  }

  hasRepProgress(reviewType) {
    const {reviewTypeAnswers} = this.state;
    if (reviewTypeAnswers.size === 0) {
      return false;
    }
    const repQuestions = reviewType
      ? reviewType.questions.filter(question => question.respondent === RESPONDENT_TYPE.REP)
      : List();
    const repQuestionIds = repQuestions.map(question => question.id).toSet();
    return reviewTypeAnswers.some((answer, questionId) => repQuestionIds.has(questionId));
  }

  onChangeReviewType(coachingReviewTypeId) {
    const {isCoach, sessionUser, review, rectifyPlaylist} = this.props;
    if (isCoach) {
      // Only the coach's changes persist to the server. We let reps preview alternative scenarios,
      // but they can't change the review type. After updating, get upcoming agenda again, since
      // the change could affect the sidebar on the right.
      CoachingReviewActions.updateReview(review.set('reviewTypeId', coachingReviewTypeId)).then(
        () => {
          UpcomingReviewsActions.upcomingReviews(
            sessionUser.id,
            UPCOMING_REVIEW_ROLES.COACH,
            undefined,
            undefined,
            review.id
          );
          rectifyPlaylist();
        }
      );
    }
    this.setState({coachingReviewTypeId});
  }

  /**
   * Has the rep answered any of their questions already
   */
  repHasAnswered() {
    const {reviewTypeAnswers} = this.state;
    const reviewType = this.getReviewType();
    if (!reviewType || !reviewTypeAnswers) {
      return false;
    }
    if (reviewTypeAnswers.isEmpty()) {
      return false;
    }

    const repQuestions = reviewType.questions.filter(q => q.respondent === RESPONDENT_TYPE.REP);
    return anyQuestionHasAnAnswer(repQuestions, reviewTypeAnswers);
  }

  getReviewType() {
    const {review, coachingReviewTypes, isCoach} = this.props;
    const {coachingReviewTypeId: stateReviewTypeId} = this.state;

    // this is complicated. If you're a coach, let's 100% rely on what's on the review,
    // because you set and send to the server the review type.
    // if you're a rep, we just rely on what's in state (unless the coach has set the review type,
    // in which case we will only show that)
    const reviewTypeId = isCoach || review.reviewTypeId ? review.reviewTypeId : stateReviewTypeId;
    return coachingReviewTypes.find(type => type.id === reviewTypeId);
  }

  onStartReview() {
    this.setState({disabled: false});
    this.startReview();
    CoachingPlaylistActions.hide();
  }

  renderStartButton(isCoach, reviewType) {
    if (isCoach) {
      return (
        <Button
          variant="contained"
          color="primary"
          className="start-meeting-button ev-button"
          onClick={this.onStartReview}>
          {START_LABEL_COACH}
        </Button>
      );
    } else {
      // disable the rep button if there is nothing for them to do.
      // nothing -> there are no rep questions to fill
      const disable = !reviewType.hasRepQuestions();

      return (
        <Button
          variant="contained"
          color="primary"
          className="start-meeting-button ev-button"
          onClick={this.onStartReview}
          disabled={disable}>
          {this.repHasAnswered() ? 'Edit Answers' : 'Answer Questions'}
        </Button>
      );
    }
  }

  render() {
    const {
      plan,
      review,
      sessionUser,
      users,
      calls,
      coachingReviewTypes,
      toggleModify,
      isCoach,
      isRecipient,
      editFeedback,
    } = this.props;

    const {
      reviewFileUploads,
      submitting,
      disabled,
      coachOverrideRepQuestions,
      isPrepping,
      reviewTypeAnswers,
      errors,
    } = this.state;

    const otherUserId = Set([plan.recipient.info.id, review.coachUserId]).find(
      id => id !== sessionUser.id
    );
    const otherUser = users.get(otherUserId) ?? sessionUser;

    const reviewType = this.getReviewType();
    const reviewTypeId = reviewType && reviewType.id;

    // block submit if they haven't filled out enough yet, or if the answers to questions are not
    // valid.
    // generally if they've started filling out the review at all,
    // unblock the button. the individual review items are optional, we
    // just want to prevent an accidental click before they really get into it
    const blockSubmit = this.blockSubmit(isCoach, reviewType, reviewTypeAnswers, reviewFileUploads);
    // Can't start review w/o a review type
    // As coach, must select one. As a recipient, can only start if the review type is set on the review.
    const hideStartButton = isCoach ? !reviewType : !review.reviewTypeId;

    const inProgress = isCoach && !disabled;

    return (
      <section className="submit-review-feedback">
        <CoachingReviewInfoBar
          plan={plan}
          review={review}
          users={users}
          sessionUser={sessionUser}
          inProgress={inProgress}
          toggleModify={toggleModify}
          isRecipient={isRecipient}
        />
        <div className="content">
          <div className="review-controls">
            <div className="button-container">
              {!hideStartButton && disabled && this.renderStartButton(isCoach, reviewType)}
              {!hideStartButton && disabled && (
                <GenericTooltip
                  hoverBehavior
                  delay={TOOLTIP_DELAY}
                  tooltip={
                    "Prepare by answering questions ahead of time. This won't count towards coaching meeting time in the dashboard."
                  }>
                  {isCoach && (
                    <Button
                      variant="outlined"
                      className="prep-meeting-button ev-button"
                      onClick={this.onPrepReview}>
                      Prepare For Meeting
                    </Button>
                  )}
                </GenericTooltip>
              )}
            </div>
            <ReviewTypeSelect
              coachingReviewTypes={coachingReviewTypes}
              reviewTypeId={reviewTypeId}
              onChange={this.onChangeReviewType}
              // Recipient cannot preview alternate scenarios once review type is set.
              // Coach cannot change review type once rep has started working.
              disabled={
                !disabled ||
                (isRecipient && !!review.reviewTypeId) ||
                (isCoach && this.hasRepProgress(reviewType))
              }
            />
          </div>
          <CoachingReviewStatusRibbon
            inProgress={!disabled}
            isPrepping={isPrepping}
            narrow={false}
          />
          {reviewType && reviewType.includesCalls && (
            <ReviewFileUploads
              reviewFileUploads={reviewFileUploads}
              calls={calls}
              users={users}
              plan={plan}
              review={review}
              disabled={disabled}
              isCoach={isCoach}
              otherUser={otherUser}
              setFileUploadScore={(val, uploadIndex, scoreIndex) =>
                this.setState({
                  reviewFileUploads: reviewFileUploads.setIn(
                    [uploadIndex, 'reviewFeedbackList', scoreIndex],
                    val
                  ),
                })
              }
              editFeedback={editFeedback}
            />
          )}
          {reviewType && (
            <ReviewTypeQuestions
              onChange={reviewTypeAnswers => this.setState({reviewTypeAnswers})}
              answers={reviewTypeAnswers}
              errors={errors}
              reviewType={reviewType}
              reviewFileUploads={reviewFileUploads}
              disabled={disabled}
              coachOverrideRepQuestions={coachOverrideRepQuestions}
              onCoachOverride={this.onCoachOverride}
              isPrepping={isPrepping}
              isCoach={isCoach}
              isRecipient={isRecipient}
              otherUser={otherUser}
              adHoc={plan.adHoc}
            />
          )}
          {hasFlag(featureFlags.adminKpis) && hasFlag(featureFlags.coachingChartForGoals) && (
            <GoalsKpiEditableList
              plan={plan}
              inProgress={inProgress}
              repId={plan.recipient.id}
              editedKpis={this.props.editedKpis}
              setEditedKpis={this.props.setEditedKpis}
              reviewCompletedAt={review.completedAt}
            />
          )}
          {isCoach && ( // Hide this for recipients.
            <div>
              <h5>General Comments</h5>
              <section className="general-comments">
                <div className="feedback">
                  <div className="comment">
                    <label>Comment</label>
                    <div className="question">
                      What general feedback do you have for {otherUser ? otherUser.firstName : ''}?
                    </div>
                    <RichTextEditor
                      onChange={val => this.setState({generalComments: val})}
                      value={this.state.generalComments || ''}
                      disabled={disabled || isRecipient}
                    />
                  </div>
                </div>
              </section>
            </div>
          )}
          <div className="button-container">
            {plan.adHoc ? (
              <Button
                variant="contained"
                color="primary"
                className="ev-button"
                onClick={() => this.endPlan(plan)}>
                Cancel
              </Button>
            ) : (
              <Button variant="outlined" className="ev-button" onClick={this.onCancel}>
                {isCoach
                  ? disabled
                    ? 'Back'
                    : editFeedback
                    ? 'Cancel Editing'
                    : 'Cancel'
                  : disabled
                  ? 'Back'
                  : 'Cancel'}
              </Button>
            )}
            <Button
              variant="contained"
              color="primary"
              className="ev-button"
              disabled={blockSubmit || disabled || submitting}
              onClick={() =>
                this.onSubmit(review, reviewFileUploads, sessionUser, plan, reviewType)
              }>
              {isCoach && editFeedback ? 'Finish Editing' : 'Submit'}
            </Button>
          </div>
        </div>
      </section>
    );
  }
}
